package nl.staartvin.solarpower.data.exposure

import nl.staartvin.solarpower.data.model.UvPoint
import nl.staartvin.solarpower.data.util.interpolateUvi
import nl.staartvin.solarpower.data.uv.MED_BASELINE_J_PER_M2
import nl.staartvin.solarpower.data.uv.SkinType
import nl.staartvin.solarpower.data.uv.safeTimeMinutesExact

/**
 * Snapshot of "how much of today's safe UV budget have you used?"
 *
 * [medFraction] is dose absorbed / MED (the burn threshold for this skin
 * type). 0.0 = no exposure. 1.0 = right at the threshold. > 1.0 = over.
 *
 * We keep it unclamped so the UI can decide over-limit copy separately from
 * the bar fill (which caps at 1.0 per the design spec).
 */
data class DoseState(val medFraction: Double) {
    val isOverLimit: Boolean get() = medFraction >= 1.0
    val barFill: Double get() = medFraction.coerceIn(0.0, 1.0)
    val remainingFraction: Double get() = (1.0 - medFraction).coerceAtLeast(0.0)

    companion object {
        val Empty = DoseState(0.0)
    }
}

/**
 * Sum the erythemal dose absorbed across all [entries] using [samples] (the
 * chart's `allSamples`) as the ground-truth UVI-vs-time curve.
 *
 * Formula:
 *   erythemal irradiance = uvi × 25 mW/m²   →   0.025 × uvi W/m²
 *   dose = ∫ 0.025 × uvi(t) dt              (J/m²; t in seconds)
 *   MED  = 200 × medFactor                   (J/m²; type II baseline scaled)
 *   fraction = dose / MED
 *
 * Entries partially outside the sample range contribute 0 for the outside
 * portion (interpolateUvi returns null → treated as 0), which is safe: we
 * always integrate over Today's chart, and Today's samples cover the entire
 * daylight window with room to spare.
 */
fun computeDoseState(
    entries: List<ExposureEntry>,
    samples: List<UvPoint>,
    skinType: SkinType,
): DoseState {
    if (entries.isEmpty() || samples.isEmpty()) return DoseState.Empty
    // Each outing's absorbed dose is divided by its sunscreen factor —
    // an SPF-30 walk delivers ~1/30th of the erythemal dose to the skin.
    val uviSeconds = entries.sumOf { entryUviSeconds(samples, it) }
    val doseJ = uviSeconds * ERYTHEMAL_IRRADIANCE_PER_UVI
    val medJ = MED_BASELINE_J_PER_M2 * skinType.medFactor
    return DoseState(doseJ / medJ)
}

/**
 * Minutes of safe sun remaining at the given [uvi], given the current dose
 * [state], [skinType], and the sunscreen [spf] the user plans to wear.
 * Returns null when UVI is too low to matter (no burn risk in any practical
 * timeframe). Returns 0 when already over budget.
 *
 * [spf] stretches the projection, but not by a flat multiple: sunscreen wears
 * off (see [effectiveSpf]), so we walk forward a minute at a time, spending
 * budget at the protected rate — which climbs back toward the bare-skin rate
 * as the SPF decays. Capped at [MAX_PROJECTION_MINUTES] so a heavily-stretched
 * estimate resolves to "all day" instead of looping for hours.
 */
fun minutesLeftAtUvi(state: DoseState, uvi: Double, skinType: SkinType, spf: Double): Int? {
    val full = safeTimeMinutesExact(uvi, skinType) ?: return null
    if (state.isOverLimit) return 0
    var budget = state.remainingFraction            // fraction of MED still available
    if (budget <= 0.0) return 0
    var minutes = 0
    while (minutes < MAX_PROJECTION_MINUTES && budget > 0.0) {
        budget -= 1.0 / (full * effectiveSpf(spf, minutes.toDouble()))
        minutes++
    }
    return minutes
}

/**
 * Each entry's share (0..1) of today's total logged erythemal dose, aligned
 * to [entries] order. SPF-aware, so a protected outing shows a smaller
 * slice. All-zero when nothing was absorbed (e.g. outings logged at night).
 *
 * skinType and the physical constants cancel in the ratio, so they don't
 * appear here — only the per-entry UVI integral and SPF matter.
 */
fun exposureShares(entries: List<ExposureEntry>, samples: List<UvPoint>): List<Double> {
    if (entries.isEmpty() || samples.isEmpty()) return entries.map { 0.0 }
    val doses = entries.map { entryUviSeconds(samples, it) }
    val total = doses.sum()
    return if (total <= 0.0) entries.map { 0.0 } else doses.map { it / total }
}

/**
 * Erythemally-weighted UVI×seconds absorbed during one outing, accounting for
 * sunscreen that decays over the outing (see [effectiveSpf]). Trapezoidal
 * integration with 1-minute steps over the chart's UVI curve — plenty fine
 * for our linearly-interpolated data, and cheap to recompute on every update.
 *
 * Decay is measured from the outing's start, i.e. we assume the user applied
 * sunscreen when they went out and didn't reapply.
 */
private fun entryUviSeconds(samples: List<UvPoint>, entry: ExposureEntry): Double {
    val start = entry.startMillis
    val end = entry.endMillis
    if (end <= start) return 0.0
    val stepMs = 60_000L
    var t = start
    var acc = 0.0
    var protectedPrev = (interpolateUvi(samples, t) ?: 0.0) / effectiveSpf(entry.spf, 0.0)
    while (t < end) {
        val next = minOf(t + stepMs, end)
        val elapsedMin = (next - start) / 60_000.0
        val protectedNext = (interpolateUvi(samples, next) ?: 0.0) / effectiveSpf(entry.spf, elapsedMin)
        acc += (protectedPrev + protectedNext) / 2.0 * (next - t) / 1000.0
        t = next
        protectedPrev = protectedNext
    }
    return acc
}

/**
 * Effective sunscreen factor [elapsedMinutes] into wear, from a fresh
 * application at [spf]. Protection fades linearly to 1.0 (bare skin) over
 * [SPF_DECAY_MINUTES]: sweat, towelling and UV breakdown mean even SPF 50 is
 * essentially gone after ~4 hours on a single application. A crude but honest
 * model — real decay isn't linear, but "it wears off" beats "it lasts forever."
 */
private fun effectiveSpf(spf: Double, elapsedMinutes: Double): Double {
    if (spf <= 1.0) return 1.0
    val decayed = spf - (spf - 1.0) * (elapsedMinutes / SPF_DECAY_MINUTES)
    return decayed.coerceAtLeast(1.0)
}

// 1 UV index unit ≈ 25 mW/m² of erythemally-weighted irradiance → 0.025 W/m².
private const val ERYTHEMAL_IRRADIANCE_PER_UVI = 0.025
private const val SPF_DECAY_MINUTES = 240.0     // ~4 h from full protection to none
private const val MAX_PROJECTION_MINUTES = 600  // 10 h; display caps this at "all day"
